import type {
    User,
    Role,
    CreatorLivestreamDto,
    UserListItem,
    CreatorAgencyProfileInfoDto,
    CreatorProfileInfoDto,
    CreatorQuickInfoDto,
    LinkedAccountRef
, UsernameHistoryDto } from '@/types/User'
import type { CreateUserRequest } from '@/types/CreateUserRequest'
import type { UpdateUserRequest } from '@/types/UpdateUserRequest'
import type { CreatorTaskProgressDto } from '@/types/TaskProgress'
import type { Wallet, WalletTransaction, SupportGiftRequest } from '@/types/Wallet'
import { API_ENDPOINTS } from '@/config/apiConstants'
import { BaseApiClient } from './BaseApiClient'
import { Permission } from '@/types/Permissions'
import type { Page } from '@/types/Page'

// Role to default permissions mapping (read-only for display purposes)
const rolePermissions: Record<Role, string[]> = {
    ADMIN: [Permission.USER_READ, Permission.USER_MANAGE],
    MANAGER: [Permission.USER_READ],
    CREATORS: [],
    PARTNER: []
}

class UserService extends BaseApiClient {
    /**
     * Get all users with pagination and optional search query
     */
    async getUsers(page: number = 0, size: number = 10, query?: string, roles?: string[], statuses?: string[], segments?: string[], managers?: string[], categories?: string[], provinces?: number[], districts?: number[], loginPlatforms?: string[], hasBackup?: boolean, live?: boolean): Promise<Page<UserListItem>> {
        const params = {
            page,
            size,
            ...(query ? { q: query } : {}),
            ...(roles && roles.length > 0 ? { role: roles } : {}),
            ...(statuses && statuses.length > 0 ? { status: statuses } : {}),
            ...(segments && segments.length > 0 ? { segment: segments } : {}),
            ...(managers && managers.length > 0 ? { manager: managers } : {}),
            ...(categories && categories.length > 0 ? { category: categories } : {}),
            ...(provinces && provinces.length > 0 ? { province: provinces } : {}),
            ...(districts && districts.length > 0 ? { district: districts } : {}),
            ...(loginPlatforms && loginPlatforms.length > 0 ? { loginPlatform: loginPlatforms } : {}),
            ...(hasBackup ? { hasBackup: true } : {}),
            ...(live ? { live: true } : {})
        }
        return this.get<Page<UserListItem>>(API_ENDPOINTS.USERS.BASE, { params })
    }

    /**
     * Create a new user
     * Note: Permissions are automatically assigned based on role by backend
     */
    async createUser(userData: CreateUserRequest): Promise<User> {
        return this.post<User>(API_ENDPOINTS.USERS.BASE, userData)
    }

    /**
     * Update an existing user
     */
    async updateUser(id: string, updates: UpdateUserRequest): Promise<User> {
        return this.put<User>(API_ENDPOINTS.USERS.BY_ID(id), updates)
    }

    /**
     * Get a user by ID
     */
    async getUserById(id: string): Promise<User> {
        return this.get<User>(API_ENDPOINTS.USERS.BY_ID(id))
    }

    /**
     * Delete a user (soft delete)
     */
    async deleteUser(id: string): Promise<void> {
        return this.delete(API_ENDPOINTS.USERS.BY_ID(id))
    }

    /**
     * Link an account as a backup of the given primary account.
     */
    async linkBackup(primaryId: string, backupUserId: string): Promise<void> {
        return this.post(`${API_ENDPOINTS.USERS.BASE}/${primaryId}/backups`, { backupUserId })
    }

    /**
     * Unlink a backup account (it becomes an independent primary again).
     */
    async unlinkBackup(backupId: string): Promise<void> {
        return this.delete(`${API_ENDPOINTS.USERS.BASE}/${backupId}/backup`)
    }

    /**
     * Promote a backup account to primary. The old primary and its other
     * backups are re-pointed to this account.
     */
    async makePrimary(backupId: string): Promise<void> {
        return this.put(`${API_ENDPOINTS.USERS.BASE}/${backupId}/make-primary`, {})
    }

    /**
     * Get suggested link candidates (same-phone independent accounts) for a user.
     */
    async getLinkCandidates(userId: string): Promise<LinkedAccountRef[]> {
        return this.get<LinkedAccountRef[]>(`${API_ENDPOINTS.USERS.BASE}/${userId}/link-candidates`)
    }

    /**
     * Get default permissions for a role (for display purposes only)
     */
    getRolePermissions(role: Role): string[] {
        return rolePermissions[role] || []
    }

    /**
     * Get mapped managers (system manager users joined via manager_tiktok_mapping).
     * Source of truth for both UsersView and RequestsView manager filter dropdowns.
     */
    async getManagers(): Promise<import('@/types/Request').ManagerInfo[]> {
        return this.get<import('@/types/Request').ManagerInfo[]>(API_ENDPOINTS.METADATA.MANAGERS)
    }

    /**
    /**
     * Get profile info for a user
     */
    async getProfileInfo(userId: string): Promise<CreatorProfileInfoDto> {
        return this.get<CreatorProfileInfoDto>(API_ENDPOINTS.USER_PROFILE_DETAIL.PROFILE_INFO(userId))
    }

    /** Kullanıcı adı değişiklik geçmişi (yeni → eski sıralı). */
    async getUsernameHistory(userId: string): Promise<UsernameHistoryDto[]> {
        return this.get<UsernameHistoryDto[]>(API_ENDPOINTS.USERS.USERNAME_HISTORY(userId))
    }

    async getQuickInfo(userId: string): Promise<CreatorQuickInfoDto> {
        return this.get<CreatorQuickInfoDto>(API_ENDPOINTS.USER_PROFILE_DETAIL.QUICK_INFO(userId))
    }

    /**
     * Get agency info for a user
     */
    async getAgencyInfo(userId: string): Promise<CreatorAgencyProfileInfoDto> {
        return this.get<CreatorAgencyProfileInfoDto>(API_ENDPOINTS.USER_PROFILE_DETAIL.AGENCY_INFO(userId))
    }

    /**
     * Get livestreams for a user
     */
    async getLiveStreams(userId: string): Promise<CreatorLivestreamDto[]> {
        return this.get<CreatorLivestreamDto[]>(API_ENDPOINTS.USER_PROFILE_DETAIL.LIVESTREAMS(userId))
    }

    /**
     * Change a user's password
     */
    async changePassword(userId: string, newPassword: string): Promise<void> {
        return this.post(API_ENDPOINTS.USERS.CHANGE_PASSWORD(userId), { password: newPassword })
    }

    /**
     * Get task progress for a user by cycle month
     */
    async getTaskProgress(userId: string, month?: string): Promise<CreatorTaskProgressDto> {
        const params = month ? { month } : {}
        return this.get<CreatorTaskProgressDto>(API_ENDPOINTS.USER_PROFILE_DETAIL.TASK_PROGRESS(userId), { params })
    }

    /**
     * Get available task progress months for a user
     */
    async getTaskProgressMonths(userId: string): Promise<string[]> {
        return this.get<string[]>(API_ENDPOINTS.USER_PROFILE_DETAIL.TASK_PROGRESS_MONTHS(userId))
    }

    /**
     * Get wallet information for a user
     */
    async getWallet(userId: string): Promise<Wallet> {
        return this.get<Wallet>(API_ENDPOINTS.USER_PROFILE_DETAIL.WALLET(userId))
    }

    /**
     * Get wallet transactions for a user with pagination
     */
    async getWalletTransactions(userId: string, page = 0, size = 20): Promise<Page<WalletTransaction>> {
        return this.get<Page<WalletTransaction>>(`/user-profile/${userId}/wallet/transactions`, { params: { page, size } })
    }

    /**
     * Record an off-balance support diamond entry for a creator (admin/manager only).
     * Does NOT mutate wallet balance.
     */
    async creditSupportGift(userId: string, body: SupportGiftRequest): Promise<WalletTransaction> {
        return this.post<WalletTransaction>(`/user-profile/${userId}/wallet/support-gift`, body)
    }

}

export const userService = new UserService()
